import React, {useContext, useEffect, useMemo} from 'react';
import {Lot} from "../common/types/Types";
import {Animated} from "react-animated-css";
import {CommonHelper} from "../common/CommonHelper";
import {useLot, useLotReserves, useUpdateLot} from "../api/hooks";
import PlanView from "../reserve/PlanView";
import ReserveList from "../reserve/ReserveList";
import BYDatePicker from "../common/inputs/BYDatePicker";
import {EntitySelect} from "../common/inputs/EntitySelect";
import {BYInput} from "../common/inputs/BYInput";
import {useFormik} from "formik";
import {InitialLotValues, LotSchema} from "../common/types/Schemas";
import {Link, Route, useLocation, useRouteMatch} from "react-router-dom";
import {useAlert} from "react-alert";
import {AuthContext} from "../providers/AuthProvider";
import Footer from "../common/views/Footer";
import BYSwitch from "../common/inputs/BYSwitch";
import { min } from 'date-fns';

export const LotScreen = (props) => {
    const lotId = Number(props.match.params.id);
    const alert = useAlert();
    const {getOperation} = useContext(AuthContext);

    const {data: lot = {} as Lot} = useLot(lotId);
    const {data: reserves = [], isLoading: isLoadingReserves} = useLotReserves(lotId);
    const {mutateAsync: updateLot} = useUpdateLot();
    const operation = getOperation(lot.operationRefOperation);

    const {url} = useRouteMatch();
    const {pathname} = useLocation();

    reserves.forEach(reserve => reserve.lot = lot);

    const plans = lot.plans;

    const formik = useFormik({
        initialValues: InitialLotValues,
        validationSchema: LotSchema,
        onSubmit: async (values) => {
            const errors = await formik.validateForm();

            if (Object.keys(errors).length) {
                return;
            }

            try {
                CommonHelper.completeVisitesDatesForAPI(lot, values);

                await updateLot({lot});

                alert.info(<><Link to={`/operation/${lot.operationRefOperation}/lot/${lot.refLot}`}>Lot
                    #{lot.numeroLot}</Link> sauvegardé</>);
            } catch (error) {
                const statusCode = error.response && error.response.status;

                if (statusCode === 401) {
                    alert.error('Valeurs invalides ou incomplètes');
                } else if (statusCode === 409) {
                    alert.error(error.response.data);
                } else {
                    alert.error(error.message);
                }
            }
        }
    });

    useEffect(() => {
        if (lot?.refLot) {
            CommonHelper.completeLotVisiteDates(lot);

            formik.setValues({
                refModeObtentionAdlr: lot.refModeObtentionAdlr,
                dateObtentionLvadlr: lot.dateObtentionLvadlr,
                dateEnvoiLvadlr: lot.dateEnvoiLvadlr,
                dateRefusAdlr: lot.dateRefusAdlr,
                commentaire: lot.commentaire,
                locataire: {
                    civilite: lot.locataire?.civilite,
                    nom: lot.locataire?.nom,
                    prenom: lot.locataire?.prenom,
                    telDom: lot.locataire?.telDom,
                    telMob: lot.locataire?.telMob,
                    email: lot.locataire?.email,
                },
                dateVisiteCloison: lot.visiteCloison,
                dateVisiteOpr: lot.visiteOpr,
                dateVisitePreLivraison: lot.visitePreLivraison,
                dateVisiteLivraison: lot.visiteLivraison
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lot]);


    let minDateQuits: Date | undefined;
    
    if (formik.values.dateVisiteLivraison) {
        minDateQuits = new Date(formik.values.dateVisiteLivraison);
        //add 30 days to the date
        minDateQuits.setDate(minDateQuits.getDate() + 31);
    }

    return <Animated animationIn="fadeIn" animationOut="fadeOut" isVisible={true}>

        {/*{JSON.stringify(formik.errors)}*/}
        <form onSubmit={formik.handleSubmit}>
            <div className="container">
                <div className="row my-4">
                    <div className="col-8">

                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item" aria-current="page"><Link
                                    to={`/operation/${operation?.refOperation}`}>{operation?.nomOperation}</Link></li>
                                <li className="breadcrumb-item active" aria-current="page"><h4>Lot {lot.numeroLot}</h4>
                                </li>
                            </ol>
                        </nav>

                    </div>
                    {lot.isDeletedInReferentiel &&
                        <div className="col-4 tittle-buttons">
                            <BYSwitch
                                onChange={() => {
                                }}
                                themeColor="red"
                                disabled={true}
                                checked={true}
                                title={'Supprimée'}
                            />
                        </div>
                    }
                    {!lot.isDeletedInReferentiel && <>
                        <div className="col-2">
                            <div className="tool-buttons-container">
                                <button type="submit"
                                        className="btn btn-secondary"
                                        disabled={formik.isSubmitting}
                                >
                                    Sauvegarder <i className="fa fa-save"></i>
                                </button>
                            </div>
                        </div>
                        <div className="col-2">
                            <div className="tool-buttons-container">
                                <Link type="submit"
                                      className="btn btn-warning"
                                      to={`${process.env.PUBLIC_URL}/reserve/new/${lot.refLot}`}
                                >
                                    Créer réserve <i className="fa fa-plus"></i>
                                </Link>
                            </div>
                        </div>
                    </>
                    }
                </div>


                <div className="row">
                    <div className="col input-container mr-1">
                        <h5>Lot</h5>

                        <div className="row">
                            <div className="col">
                                Nature : <b>{lot.nature}</b>
                            </div>
                            <div className="col">
                                Typologie : <b>{lot.type}</b>
                            </div>
                        </div>

                        <div className="row">

                            <div className="col">
                                Etage : <b>{lot.etage}</b>
                            </div>
                            <div className="col">
                                Bâtiment : <b>{lot.batiment}</b>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col">
                                Numéro Copro : <b>{lot.numeroCopro}</b>
                            </div>
                            <div className="col">
                                Lot secondaire : <b>{lot.lotsSecondaire}</b>
                            </div>
                        </div>

                    </div>


                    <div className="col input-container ml-1">
                        <h5>Client</h5>

                        <div className="row">
                            <div className="col">
                                Nom : <b>{CommonHelper.formatAcquereur(lot.acquereur)}</b>
                            </div>
                            <div className="col">
                                Adresse : <b>{lot.acquereur?.adresse}</b>
                            </div>
                        </div>

                        <div className="row">

                            <div className="col">
                                Adresse compl. : <b>{lot.acquereur?.complAdresse}</b>
                            </div>
                            <div className="col">
                                CP : <b>{lot.acquereur?.cp}</b>
                            </div>
                        </div>
                        <div className="row">

                            <div className="col">
                                Ville : <b>{lot.acquereur?.ville}</b>
                            </div>
                            <div className="col">
                            </div>
                        </div>
                        <div className="row">

                            <div className="col">
                                Téléphones
                                : <b>{[lot.acquereur?.telDom, lot.acquereur?.telMob, lot.acquereur?.telPro].filter(v => v).join(', ')}</b>
                            </div>
                            <div className="col">
                                Email : <b>{lot.acquereur?.email}</b>
                            </div>
                        </div>

                    </div>
                </div>


                <div className="row mt-2">
                    <div className="col input-container mr-1">
                        <h5>Quitus</h5>

                        <div className="row">
                            <div className="col">
                                <EntitySelect title={'Type'} type={'obtention-adlr'}
                                              value={formik.values.refModeObtentionAdlr}
                                              disabled={formik.values.refModeObtentionAdlr === 1}
                                              onChange={(newValue) => {
                                                  formik.setValues({
                                                      ...formik.values,
                                                      refModeObtentionAdlr: newValue
                                                  })
                                              }}
                                />
                            </div>
                            <div className="col">
                                <BYDatePicker
                                    title={'Date d\'obtention'}
                                    //required={formik.values.refModeObtentionAdlr}
                                    value={formik.values.refModeObtentionAdlr === null 
                                        ? null 
                                        : formik.values.dateObtentionLvadlr}
                                    disabled={formik.values.refModeObtentionAdlr === 1 ? true : false}
                                    minDate={minDateQuits}
                                    onChange={(newValue: Date) => {
                                        formik.setValues({
                                            ...formik.values,
                                            dateObtentionLvadlr: 
                                            newValue?.toISOString() === undefined 
                                            ? null 
                                            : newValue?.toISOString()
                                        })
                                    }}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col">
                                <BYDatePicker
                                    title={'Date d\'envoi'} value={formik.values.dateEnvoiLvadlr}
                                    onChange={(newValue: Date) => {
                                        formik.setValues({
                                            ...formik.values,
                                            dateEnvoiLvadlr: newValue?.toISOString()
                                        })
                                    }}
                                />
                            </div>
                            <div className="col">
                                <BYDatePicker title={'Date de refus'} value={formik.values.dateRefusAdlr}
                                              onChange={(newValue: Date) => {
                                                  formik.setValues({
                                                      ...formik.values,
                                                      dateRefusAdlr: newValue?.toISOString()
                                                  })
                                              }}
                                />
                            </div>
                        </div>

                        <BYInput type={'textarea'}
                                 title={'Commentaire'}
                                 rows={4}
                                 value={formik.values.commentaire}
                                 name="commentaire"
                                 onChange={formik.handleChange}
                                 onBlur={formik.handleBlur}
                                 touched={formik.touched.commentaire}
                                 error={formik.errors.commentaire}
                        />

                    </div>

                    <div className="col input-container ml-1">
                        <h5>Locataire</h5>

                        <div className="row">
                            <div className="col">
                                <EntitySelect title={'Civilité'} type={'civilite'}
                                              value={formik.values.locataire?.civilite}
                                              onChange={(newValue) => {
                                                  formik.setValues({
                                                      ...formik.values,
                                                      locataire: {
                                                          ...formik.values.locataire,
                                                          civilite: newValue
                                                      }
                                                  })
                                              }}
                                />
                            </div>
                            <div className="col">
                            </div>
                        </div>

                        <div className="row">

                            <div className="col">
                                <BYInput type={'text'} title={'Nom *'}
                                         value={formik.values.locataire?.nom}
                                         onChange={formik.handleChange}
                                         name="locataire.nom"
                                         required={formik.values.locataire?.prenom?.length > 0}
                                         onBlur={formik.handleBlur}
                                         touched={formik.touched.locataire?.nom}
                                         error={formik.errors.locataire?.nom}
                                />
                            </div>
                            <div className="col">
                                <BYInput type={'text'} title={'Prénom *'}
                                         value={formik.values.locataire?.prenom}
                                         onChange={formik.handleChange}
                                         name="locataire.prenom"
                                         required={formik.values.locataire?.nom?.length > 0}
                                         onBlur={formik.handleBlur}
                                         touched={formik.touched.locataire?.prenom}
                                         error={formik.errors.locataire?.prenom}/>
                            </div>
                        </div>
                        <div className="row">

                            <div className="col">
                                <BYInput type={'text'} title={'Tel. fixe'}
                                         value={formik.values.locataire?.telDom}
                                         onChange={formik.handleChange}
                                         name="locataire.telDom"
                                         onBlur={formik.handleBlur}
                                         touched={formik.touched.locataire?.telDom}
                                         error={formik.errors.locataire?.telDom}/>
                            </div>
                            <div className="col">
                                <BYInput type={'text'} title={'Tel. mobile'}
                                         value={formik.values.locataire?.telMob}
                                         onChange={formik.handleChange}
                                         name="locataire.telMob"
                                         onBlur={formik.handleBlur}
                                         touched={formik.touched.locataire?.telMob}
                                         error={formik.errors.locataire?.telMob}/>
                            </div>
                        </div>

                        <BYInput type={'email'} title={'Email'} value={formik.values.locataire?.email}
                                 onChange={formik.handleChange}
                                 name="locataire.email"
                                 onBlur={formik.handleBlur}
                                 touched={formik.touched.locataire?.email}
                                 error={formik.errors.locataire?.email}
                        />

                    </div>
                </div>

                <div className="input-container mt-2 row">
                    <div className="col">
                        <h5>Rendez-vous</h5>

                        <div className="row">
                            <div className="col">
                                <BYDatePicker title={'Visite Cloison'} value={formik.values.dateVisiteCloison}
                                              onChange={(newValue: Date) => {

                                                  formik.setValues({
                                                      ...formik.values,
                                                      dateVisiteCloison: newValue?.toISOString()
                                                  })
                                              }}/>
                            </div>
                            <div className="col">
                                <BYDatePicker title={'OPR'} value={formik.values.dateVisiteOpr}
                                              onChange={(newValue: Date) => {
                                                  formik.setValues({
                                                      ...formik.values,
                                                      dateVisiteOpr: newValue?.toISOString()
                                                  })
                                              }}/>
                            </div>
                            <div className="col">
                                <BYDatePicker title={'Pré-livraison'} value={formik.values.dateVisitePreLivraison}
                                              onChange={(newValue: Date) => {
                                                  formik.setValues({
                                                      ...formik.values,
                                                      dateVisitePreLivraison: newValue?.toISOString()
                                                  })
                                              }}/>
                            </div>
                            <div className="col">
                                <BYDatePicker title={'Livraison'} value={formik.values.dateVisiteLivraison}
                                              onChange={(newValue: Date) => {
                                                  formik.setValues({
                                                      ...formik.values,
                                                      dateVisiteLivraison: newValue?.toISOString()
                                                  })
                                              }}/>
                            </div>
                        </div>

                    </div>
                </div>

                <h5 className="mt-3">Réserves</h5>

            </div>

            <div className="container-fluid">

                <ul className="nav nav-tabs mt-2" role="tablist">
                    <li className="nav-item" role="presentation">
                        {/*<button type="button" role="tab"*/}
                        {/*        className={`nav-link ${tab === 0 ? 'active' : ''}`}*/}
                        {/*        onClick={() => setTab(0)}>*/}
                        {/*    Table <i className="fas fa-table"/>*/}
                        {/*</button>*/}

                        <Link to={`${url}/list`}
                              className={`nav-link ${pathname.includes('list') || pathname === url ? 'active' : ''}`}
                        >
                            Liste ({reserves?.length}) <i className="fas fa-building"/>
                        </Link>
                    </li>
                    <li className="nav-item" role="presentation">
                        {/*<button type="button" role="tab"*/}
                        {/*        className={`nav-link ${tab === 1 ? 'active' : ''}`}*/}
                        {/*        onClick={() => setTab(1)}>*/}
                        {/*    Plans <i className="fas fa-map-marked"></i>*/}
                        {/*</button>*/}
                        <Link to={`${url}/plan`}
                              className={`nav-link ${pathname.includes('plan') ? 'active' : ''}`}
                        >
                            Plans ({lot?.plans?.length}) <i className="fas fa-fire"></i>
                        </Link>
                    </li>
                </ul>

                <div className="tab-content" id="myTabContent">
                    <div className={`tab-pane fade show active`}
                         role="tabpanel"
                         aria-labelledby="home-tab">

                        <Route path={[url, `${url}/list`]} exact>
                            {useMemo(() => {
                                return <ReserveList data={reserves}
                                                    loading={isLoadingReserves}/>;
                                // eslint-disable-next-line react-hooks/exhaustive-deps
                            }, [reserves, isLoadingReserves, lot])}
                        </Route>

                        <Route path={`${url}/plan`}>
                            {useMemo(() => <div className="horizontal-scrollable px-3">
                                {plans?.map((plan) => {
                                    const planReserves = reserves.filter(reserve => reserve.planRefPlan === plan.refPlan);
                                    return <PlanView key={`plan-${plan.refPlan}`} plan={plan} reserves={planReserves}
                                                     withLink={true}/>
                                })}
                            </div>, [plans, reserves])}
                        </Route>

                    </div>
                </div>
            </div>
        </form>

        <Footer/>
    </Animated>;
}
